/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useBooking } from "./useBooking";
import { useWaitingListJoin } from "../lib/react-query/useWaitingListJoin";
import { invalidateClientTerms } from "../lib/react-query/invalidateClientTerms";
import { ROUTES } from "../constants/constants";
import { setRootFormError } from "../utils/api/apiErrors";

export const useJoinWaitingList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedServices, selectedProvider, selectedLocation, selectedCategories, selectedDate, bookingRequest } =
    useBooking();
  const { mutate, isPending } = useWaitingListJoin();

  const handleJoin = useCallback(
    (client, setError) => {
      const payload = {
        date: selectedDate,
        service_id: selectedServices[0]?.id,
        provider_id: selectedProvider?.id,
        count: selectedServices.find((service) => service.group_booking_count)?.group_booking_count || 1,
        location_id: selectedLocation?.id ?? null,
        category_id: selectedCategories?.[0]?.id ?? null,
        terms: bookingRequest.terms,
        ...(client ? { client } : {}),
      };

      mutate(payload, {
        onSuccess: () => {
          invalidateClientTerms(queryClient, payload.terms);
          navigate(ROUTES.WAITING_LIST_JOINED);
        },
        onError: (error) => {
          if (!setError) return;

          setRootFormError(setError, error);
        },
      });
    },
    [
      mutate,
      navigate,
      queryClient,
      bookingRequest.terms,
      selectedCategories,
      selectedDate,
      selectedLocation,
      selectedProvider,
      selectedServices,
    ]
  );

  return { handleJoin, isPending };
};
